import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
  Menu,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { useAuth } from '../contexts/AuthContext';
import { adminFetch } from '../utils/adminFetch';
import {
  DISABILITY_FALSE_VALUE,
  DISABILITY_TRUE_VALUE,
  DISABILITY_UNSET_VALUE,
  LANGUAGE_UNSET_VALUE,
  SUPPORTED_USER_LANGUAGES,
} from '../utils/supportedLanguages';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'student' | 'coach' | 'admin' | 'entreprise';
  isActive: boolean;
  createdAt: string;
  lastLogin?: string;
  team?: string | null;
  position?: string | null;
  defaultLanguage?: string | null;
  disability?: boolean | null;
  enrolledCourses?: number;
  assignedCourses?: number;
  company?: {
    id: string;
    name: string;
    slug: string;
  } | null;
}

interface CompanyOption {
  id: string;
  name: string;
  slug: string;
}

const SCORM_NO_EMAIL_DOMAIN = '@no-email.scorm.local';
const isScormNoEmail = (email?: string): boolean =>
  Boolean(email && email.endsWith(SCORM_NO_EMAIL_DOMAIN));

type UserFormData = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  role: 'student' | 'coach' | 'admin' | 'entreprise';
  isActive: boolean;
  companyId: string;
  team: string;
  position: string;
  defaultLanguage: string;
  disability: string;
};

const disabilityToFormValue = (value: boolean | null | undefined): string => {
  if (value === true) return DISABILITY_TRUE_VALUE;
  if (value === false) return DISABILITY_FALSE_VALUE;
  return DISABILITY_UNSET_VALUE;
};

const disabilityFromFormValue = (value: string): boolean | null => {
  if (value === DISABILITY_TRUE_VALUE) return true;
  if (value === DISABILITY_FALSE_VALUE) return false;
  return null;
};

const buildProfilePayload = (data: UserFormData) => ({
  team: data.team.trim() || null,
  position: data.position.trim() || null,
  defaultLanguage:
    data.defaultLanguage === LANGUAGE_UNSET_VALUE ? null : data.defaultLanguage,
  disability: disabilityFromFormValue(data.disability),
});

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'student' as 'student' | 'coach' | 'admin' | 'entreprise',
    isActive: true,
    companyId: '',
    team: '',
    position: '',
    defaultLanguage: LANGUAGE_UNSET_VALUE,
    disability: DISABILITY_UNSET_VALUE,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [roleFilter, setRoleFilter] = useState<'all' | 'student' | 'coach' | 'admin' | 'entreprise'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [companies, setCompanies] = useState<CompanyOption[]>([]);

  const { user } = useAuth();
  const isEditingScormNoEmailUser = Boolean(
    isEditing && selectedUser?.email && selectedUser.email.endsWith(SCORM_NO_EMAIL_DOMAIN)
  );

  const copyUserId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
    } catch {
      // ignore clipboard errors
    }
  };

  const fetchUsers = async (showPageLoader: boolean = true) => {
    try {
      if (showPageLoader) {
        setLoading(true);
      }
      setError('');
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams();
      if (roleFilter !== 'all') {
        params.append('role', roleFilter);
      }
      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }
      const queryString = params.toString();
      const response = await adminFetch(`${process.env.REACT_APP_API_URL}/users${queryString ? `?${queryString}` : ''}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Impossible de charger les utilisateurs: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('📚 Users loaded:', data);
      
      // Transform API response to match User interface
      const transformedUsers: User[] = (data.users || []).map((u: any) => ({
        id: String(u.id || u._id),
        firstName: u.firstName,
        lastName: u.lastName,
        email: u.email,
        role: u.role,
        company: u.company || null,
        team: u.team ?? null,
        position: u.position ?? null,
        defaultLanguage: u.defaultLanguage ?? null,
        disability: u.disability ?? null,
        isActive: u.isActive,
        createdAt: u.createdAt ? new Date(u.createdAt).toISOString().split('T')[0] : '',
        lastLogin: u.lastLogin || u.lastLoginDate,
        enrolledCourses: u.stats?.enrollmentCount || u.enrolledCourses,
        assignedCourses: u.stats?.assignedCourses || u.assignedCourses
      }));

      setUsers(transformedUsers);
    } catch (error: any) {
      console.error('❌ Error fetching users:', error);
      setError(error.message || 'Impossible de charger les utilisateurs');
    } finally {
      if (showPageLoader) {
        setLoading(false);
      }
    }
  };

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await adminFetch(`${process.env.REACT_APP_API_URL}/companies`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        return;
      }
      const data = await response.json();
      setCompanies(data.companies || []);
    } catch (error) {
      console.warn('Impossible de charger les entreprises', error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, userData: User) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedUser(userData);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const openUserDialog = (userData?: User) => {
    if (userData) {
      setFormData({
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        password: '',
        role: userData.role,
        isActive: userData.isActive,
        companyId: userData.company?.id || '',
        team: userData.team || '',
        position: userData.position || '',
        defaultLanguage: userData.defaultLanguage || LANGUAGE_UNSET_VALUE,
        disability: disabilityToFormValue(userData.disability),
      });
      setSelectedUser(userData);
      setIsEditing(true);
    } else {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        role: 'student',
        isActive: true,
        companyId: '',
        team: '',
        position: '',
        defaultLanguage: LANGUAGE_UNSET_VALUE,
        disability: DISABILITY_UNSET_VALUE,
      });
      setSelectedUser(null);
      setIsEditing(false);
    }
    setUserDialogOpen(true);
    handleMenuClose();
  };

  const handleSaveUser = async () => {
    try {
      setError('');
      const token = localStorage.getItem('admin_token');
      
      // Validation
      if (!formData.firstName || !formData.lastName || (!isEditingScormNoEmailUser && !formData.email)) {
        setError('Merci de remplir tous les champs obligatoires');
        return;
      }

      if (!isEditing && !formData.password) {
        setError('Le mot de passe est obligatoire pour les nouveaux utilisateurs');
        return;
      }

      if (isEditing && selectedUser) {
        // Update user
        const response = await adminFetch(`${process.env.REACT_APP_API_URL}/users/${selectedUser.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            firstName: formData.firstName,
            lastName: formData.lastName,
            ...(isEditingScormNoEmailUser ? {} : { email: formData.email }),
            role: formData.role,
            isActive: formData.isActive,
            companyId: formData.role === 'student' || formData.role === 'entreprise'
              ? (formData.companyId || null)
              : null,
            ...buildProfilePayload(formData),
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Impossible de mettre à jour l'utilisateur");
        }

        const data = await response.json();
        console.log('✅ User updated:', data);
        
        // Refresh users list
        await fetchUsers(false);
      } else {
        // Create new user
        const response = await adminFetch(`${process.env.REACT_APP_API_URL}/users`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            password: formData.password,
            role: formData.role,
            isActive: formData.isActive,
            companyId: formData.role === 'student' || formData.role === 'entreprise'
              ? (formData.companyId || null)
              : null,
            ...buildProfilePayload(formData),
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Impossible de créer l'utilisateur");
        }

        const data = await response.json();
        console.log('✅ User created:', data);
        
        // Refresh users list
        await fetchUsers(false);
      }
      
      setUserDialogOpen(false);
    } catch (error: any) {
      console.error('❌ Error saving user:', error);
      setError(error.message || "Impossible d'enregistrer l'utilisateur");
    }
  };

  const handleDeleteUser = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const confirmDeleteUser = async () => {
    if (selectedUser) {
      try {
        setError('');
        const token = localStorage.getItem('admin_token');
        
        const response = await adminFetch(`${process.env.REACT_APP_API_URL}/users/${selectedUser.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Impossible de supprimer l'utilisateur");
        }

        console.log('✅ User deleted');
        
        // Refresh users list
        await fetchUsers(false);
        setDeleteDialogOpen(false);
        setSelectedUser(null);
      } catch (error: any) {
        console.error('❌ Error deleting user:', error);
        setError(error.message || "Impossible de supprimer l'utilisateur");
      }
    }
  };

  const toggleUserStatus = async (userData: User) => {
    try {
      setError('');
      const token = localStorage.getItem('admin_token');
      
      const response = await adminFetch(`${process.env.REACT_APP_API_URL}/users/${userData.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          isActive: !userData.isActive
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Impossible de mettre à jour le statut de l'utilisateur");
      }

      console.log('✅ User status updated');
      
      // Refresh users list
      await fetchUsers(false);
    } catch (error: any) {
      console.error('❌ Error updating user status:', error);
      setError(error.message || "Impossible de mettre à jour le statut de l'utilisateur");
    }
  };

  const getRoleChip = (role: string) => {
    const colors = {
      admin: 'error',
      coach: 'primary',
      student: 'default',
      entreprise: 'secondary',
    };
    const labels: Record<string, string> = {
      entreprise: 'Entreprise',
    };
    return (
      <Chip
        label={labels[role] || role.charAt(0).toUpperCase() + role.slice(1)}
        color={colors[role as keyof typeof colors] as any}
        size="small"
        variant="outlined"
      />
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Utilisateur',
      width: 220,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%',
            lineHeight: 1.1
          }}
        >
          <Typography variant="body2" fontWeight="500" sx={{ lineHeight: 1.2 }}>
            {params.row.firstName} {params.row.lastName}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: '0.8rem', lineHeight: 1.1, mt: 0.2 }}
          >
            {isScormNoEmail(params.row.email) ? '' : params.row.email}
          </Typography>
          {isScormNoEmail(params.row.email) && (
            <Chip
              label="SCORM sans email"
              size="small"
              color="warning"
              variant="outlined"
              sx={{ mt: 0.5, width: 'fit-content' }}
            />
          )}
        </Box>
      )
    },
    {
      field: 'role',
      headerName: 'Rôle',
      width: 120,
      renderCell: (params) => getRoleChip(params.value)
    },
    {
      field: 'company',
      headerName: 'Entreprise',
      width: 180,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="body2" align="center">
            {params.row.role === 'student' || params.row.role === 'entreprise'
              ? (params.row.company?.name || '-')
              : '-'}
          </Typography>
        </Box>
      )
    },
    {
      field: 'isActive',
      headerName: 'Statut',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Actif' : 'Inactif'}
          color={params.value ? 'success' : 'default'}
          size="small"
          icon={params.value ? <CheckCircleIcon /> : <BlockIcon />}
        />
      )
    },
    {
      field: 'courses',
      headerName: 'Formations',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="body2" align="center">
            {params.row.enrolledCourses || 0}
          </Typography>
        </Box>
      )
    },
    {
      field: 'createdAt',
      headerName: 'Créé le',
      width: 120,
      renderCell: (params) => (
        <Typography variant="caption" color="text.secondary">
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      )
    },
    {
      field: 'lastLogin',
      headerName: 'Dernière connexion',
      width: 120,
      renderCell: (params) => (
        <Typography variant="caption" color="text.secondary">
          {params.value ? new Date(params.value).toLocaleDateString() : 'Jamais'}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => handleMenuOpen(e, params.row)}
        >
          <MoreVertIcon />
        </IconButton>
      )
    }
  ];

  // Only show if user is admin
  if (user?.role !== 'admin') {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Alert severity="error">
            Accès refusé. Seuls les administrateurs peuvent gérer les utilisateurs.
          </Alert>
        </Box>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Gestion des utilisateurs
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gérez les apprenants, coachs et administrateurs
          </Typography>
        </Box>
        <Box display="flex" gap={2} alignItems="center">
          <Box
            component="form"
            display="flex"
            gap={2}
            alignItems="center"
            onSubmit={(e) => {
              e.preventDefault();
              void fetchUsers();
            }}
          >
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Rôle</InputLabel>
              <Select
                label="Rôle"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value as any)}
              >
                <MenuItem value="all">Tous les rôles</MenuItem>
                <MenuItem value="student">Apprenants</MenuItem>
                <MenuItem value="coach">Coachs</MenuItem>
                <MenuItem value="entreprise">Entreprises</MenuItem>
                <MenuItem value="admin">Admins</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Recherche"
              placeholder="Nom, prénom, email ou ID"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Button type="submit" variant="outlined">
              Filtrer
            </Button>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openUserDialog()}
            size="large"
          >
            Ajouter un utilisateur
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Box display="flex" gap={2} mb={4} flexWrap="wrap">
        <Card sx={{ minWidth: 150 }}>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h5" fontWeight="bold">
              {users.filter(u => u.role === 'admin').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Administrateurs
            </Typography>
          </CardContent>
        </Card>
        
        <Card sx={{ minWidth: 150 }}>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h5" fontWeight="bold">
              {users.filter(u => u.role === 'coach').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Coachs
            </Typography>
          </CardContent>
        </Card>
        
        <Card sx={{ minWidth: 150 }}>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h5" fontWeight="bold">
              {users.filter(u => u.role === 'entreprise').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Comptes entreprise
            </Typography>
          </CardContent>
        </Card>
        
        <Card sx={{ minWidth: 150 }}>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h5" fontWeight="bold">
              {users.filter(u => u.role === 'student').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Apprenants
            </Typography>
          </CardContent>
        </Card>
        
        <Card sx={{ minWidth: 150 }}>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h5" fontWeight="bold">
              {users.filter(u => u.isActive).length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Utilisateurs actifs
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Users Table */}
      <Card>
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={users}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            pageSizeOptions={[5, 10, 25]}
            disableRowSelectionOnClick
            slots={{
              toolbar: GridToolbar,
            }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f0f0f0',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#fafafa',
                borderBottom: '2px solid #e0e0e0',
              },
            }}
          />
        </Box>
      </Card>

      {/* User Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => openUserDialog(selectedUser!)}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Modifier l'utilisateur
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedUser) void copyUserId(String(selectedUser.id));
            handleMenuClose();
          }}
        >
          <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} />
          Copier l’ID
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedUser) toggleUserStatus(selectedUser);
          handleMenuClose();
        }}>
          {selectedUser?.isActive ? <BlockIcon /> : <CheckCircleIcon />}
          <Box ml={1}>
            {selectedUser?.isActive ? 'Désactiver' : 'Activer'}
          </Box>
        </MenuItem>
        <MenuItem onClick={handleDeleteUser} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Supprimer l'utilisateur
        </MenuItem>
      </Menu>

      {/* User Dialog */}
      <Dialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isEditing ? 'Modifier l’utilisateur' : 'Ajouter un utilisateur'}
        </DialogTitle>
        <DialogContent>
          {isEditingScormNoEmailUser && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Compte SCORM sans email: le champ email est masqué.
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Prénom"
            fullWidth
            variant="outlined"
            value={formData.firstName}
            onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
          />
          <TextField
            margin="dense"
            label="Nom"
            fullWidth
            variant="outlined"
            value={formData.lastName}
            onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
          />
          {!isEditingScormNoEmailUser && (
            <TextField
              margin="dense"
              label="Email"
              type="email"
              fullWidth
              variant="outlined"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            />
          )}
          {!isEditing && (
            <TextField
              margin="dense"
              label="Mot de passe"
              type="password"
              fullWidth
              variant="outlined"
              value={formData.password}
              onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
              required
              helperText="Le mot de passe est requis pour les nouveaux utilisateurs"
            />
          )}
          <FormControl fullWidth margin="dense">
            <InputLabel>Rôle</InputLabel>
            <Select
              value={formData.role}
              label="Rôle"
              onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value as any }))}
            >
              <MenuItem value="student">Apprenant</MenuItem>
              <MenuItem value="coach">Coach</MenuItem>
              <MenuItem value="entreprise">Entreprise</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
          {(formData.role === 'student' || formData.role === 'entreprise') && (
            <FormControl fullWidth margin="dense">
              <InputLabel>
                {formData.role === 'entreprise' ? 'Entreprise' : 'Entreprise (optionnel)'}
              </InputLabel>
              <Select
                value={formData.companyId}
                label={formData.role === 'entreprise' ? 'Entreprise' : 'Entreprise (optionnel)'}
                onChange={(e) => setFormData(prev => ({ ...prev, companyId: e.target.value }))}
              >
                <MenuItem value="">Aucune entreprise</MenuItem>
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            margin="dense"
            label="Équipe (optionnel)"
            fullWidth
            variant="outlined"
            value={formData.team}
            onChange={(e) => setFormData(prev => ({ ...prev, team: e.target.value }))}
          />
          <TextField
            margin="dense"
            label="Poste (optionnel)"
            fullWidth
            variant="outlined"
            value={formData.position}
            onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Handicap (optionnel)</InputLabel>
            <Select
              value={formData.disability}
              label="Handicap (optionnel)"
              onChange={(e) => setFormData(prev => ({ ...prev, disability: e.target.value }))}
            >
              <MenuItem value={DISABILITY_UNSET_VALUE}>Non défini</MenuItem>
              <MenuItem value={DISABILITY_TRUE_VALUE}>Oui</MenuItem>
              <MenuItem value={DISABILITY_FALSE_VALUE}>Non</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Langue (optionnel)</InputLabel>
            <Select
              value={formData.defaultLanguage}
              label="Langue (optionnel)"
              onChange={(e) => setFormData(prev => ({ ...prev, defaultLanguage: e.target.value }))}
            >
              <MenuItem value={LANGUAGE_UNSET_VALUE}>Non définie</MenuItem>
              {SUPPORTED_USER_LANGUAGES.map((lang) => (
                <MenuItem key={lang.code} value={lang.code}>
                  {lang.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
              />
            }
            label="Utilisateur actif"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>Annuler</Button>
          <Button onClick={handleSaveUser} variant="contained">
            {isEditing ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue de confirmation de suppression */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Supprimer l'utilisateur</DialogTitle>
        <DialogContent>
          <Typography>
            Confirmez-vous la suppression de {selectedUser?.firstName} {selectedUser?.lastName} ?
            Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Annuler</Button>
          <Button onClick={confirmDeleteUser} color="error" variant="contained">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Users; 